import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  FormHelperText,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  OutlinedInput,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getCountries, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';

const displayNames =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const ALL_COUNTRIES = getCountries()
  .map((code) => {
    try {
      return {
        code,
        dialCode: `+${getCountryCallingCode(code)}`,
        name: displayNames ? displayNames.of(code) : code,
      };
    } catch {
      return null;
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.name.localeCompare(b.name));

const FlagCircle = ({ countryCode, size = 24 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <ReactCountryFlag
      countryCode={countryCode}
      svg
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  </Box>
);

const CountryPhoneInput = ({
  countryCode = 'IN',
  phoneNumber = '',
  onChange,
  label = 'Phone Number',
  error = false,
  helperText,
  disabled = false,
  ...rest
}) => {
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCountry = useMemo(
    () => ALL_COUNTRIES.find((c) => c.code === countryCode) || ALL_COUNTRIES[0],
    [countryCode],
  );

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const filteredCountries = useMemo(() => {
    const term = search.toLowerCase();
    let list = term
      ? ALL_COUNTRIES.filter(
          (c) =>
            c.name.toLowerCase().includes(term) ||
            c.dialCode.includes(search),
        )
      : [...ALL_COUNTRIES];

    if (!term && selectedCountry) {
      const idx = list.findIndex((c) => c.code === selectedCountry.code);
      if (idx > 0) {
        const [item] = list.splice(idx, 1);
        list.unshift(item);
      }
    }
    return list;
  }, [search, selectedCountry]);

  const handleCountrySelect = (country) => {
    onChange('countryCode', country.code);
    setOpen(false);
    setSearch('');
  };

  const handlePhoneInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    onChange('phoneNumber', digits);
  };

  const displayValue = useMemo(() => {
    if (!phoneNumber) return '';
    try {
      const parsed = parsePhoneNumber(phoneNumber, selectedCountry.code);
      if (parsed) return parsed.format('NATIONAL');
    } catch {
      /* fall through */
    }
    return phoneNumber;
  }, [phoneNumber, selectedCountry]);

  const popperId = open ? 'country-phone-popper' : undefined;

  return (
    <Box sx={{ width: '100%' }} {...rest}>

      {/* Label above the box — same style as First Name / Last Name */}
      <Typography
        variant="body2"
        component="label"
        sx={{
          display: 'block',
          mb: 0.75,
          fontSize: '0.8125rem',
          color: error ? 'error.main' : 'text.secondary',
          fontFamily: 'inherit',
        }}
      >
        {label}
      </Typography>

      <OutlinedInput
        ref={anchorRef}
        fullWidth
        value={displayValue}
        onChange={handlePhoneInput}
        placeholder="00000 00000"
        error={error}
        disabled={disabled}
        inputProps={{ inputMode: 'numeric' }}
        startAdornment={
          <InputAdornment position="start" sx={{ mr: 0 }}>
            <Button
              disableRipple
              disabled={disabled}
              aria-describedby={popperId}
              onClick={() => { setOpen((o) => !o); setSearch(''); }}
              sx={{
                minWidth: 0,
                px: 0.5,
                py: 0,
                gap: 0.5,
                color: 'text.primary',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <FlagCircle countryCode={selectedCountry.code} />
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{
                  color: 'text.secondary',
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Button>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 0.75, userSelect: 'none', whiteSpace: 'nowrap' }}
            >
              {selectedCountry.dialCode}
            </Typography>

            <Divider orientation="vertical" flexItem sx={{ mr: 1, my: 0.5 }} />
          </InputAdornment>
        }
      />

      {helperText && (
        <FormHelperText error={error} sx={{ mx: '14px' }}>
          {helperText}
        </FormHelperText>
      )}

      {/* Dropdown — unchanged */}
      <Popper
        id={popperId}
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 'modal' }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={4}
            sx={{
              width: anchorRef.current?.clientWidth || 280,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'shape.borderRadius',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search country or dial code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                inputProps={{ 'aria-label': 'Search country' }}
              />
            </Box>

            <List
              dense
              disablePadding
              sx={{
                maxHeight: 220,
                overflowY: 'auto',
                py: 0.5,
                '&::-webkit-scrollbar': { width: 5 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.300',
                  borderRadius: 4,
                },
              }}
            >
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => {
                  const isSelected = selectedCountry?.code === country.code;
                  return (
                    <ListItem
                      key={country.code}
                      component="button"
                      onClick={() => handleCountrySelect(country)}
                      selected={isSelected}
                      sx={{
                        width: '100%',
                        border: 'none',
                        cursor: 'pointer',
                        bgcolor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <FlagCircle countryCode={country.code} size={20} />
                      <ListItemText
                        primary={country.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          noWrap: true,
                          color: 'text.primary',
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0 }}
                      >
                        {country.dialCode}
                      </Typography>
                      {isSelected && (
                        <CheckIcon
                          sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }}
                        />
                      )}
                    </ListItem>
                  );
                })
              ) : (
                <ListItem sx={{ justifyContent: 'center' }}>
                  <ListItemText
                    primary="No results"
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CountryPhoneInput;